import { ResourceTableOption } from "PFApp/components/resource_table_toggle";
import { ActionCellData } from "PFApp/components/resource_table_toggle";
import { LOCKED_SHORTLIST_STATES } from "PFApp/constants/shortlist_states";
import { Button } from "PFComponents/button";
import { Flex } from "PFComponents/containers";
import { Pill } from "PFComponents/pill";
import { useErrorsGrowl } from "PFCore/hooks";
import { useInvalidateManageViewRoles } from "PFCore/hooks/queries/roles/use_invalidate_manage_view_roles";
import { useShortlistCreate } from "PFCore/hooks/queries/shortlists/use_shortlist_create";
import { useShortlistUpdate } from "PFCore/hooks/queries/shortlists/use_shortlist_update";
import { Shortlist, ShortlistSuggested } from "PFTypes";
import { Activity } from "PFTypes/activity";
import { useTranslation } from "react-i18next";

type SuggestActionCellProps = {
  data: ActionCellData;
  option: ResourceTableOption;
  activity: Activity;
  shortlists: Shortlist[];
  disabled?: boolean;
};

export const SuggestActionCell = ({
  data,
  option,
  activity,
  shortlists,
  disabled
}: SuggestActionCellProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles.suggestedWmSidePanel" });
  const growlErrors = useErrorsGrowl();

  const { invalidateAll: invalidateManageViewRoles } = useInvalidateManageViewRoles();
  const { mutate: createShortlist, isPending: isCreatePending } = useShortlistCreate({
    onSuccess: invalidateManageViewRoles,
    onError: (error) => growlErrors(error)
  });
  const { mutate: updateShortlist, isPending: isUpdatePending } = useShortlistUpdate({
    activityId: activity.id,
    options: {
      onSuccess: invalidateManageViewRoles,
      onError: (error) => growlErrors(error)
    }
  });
  const isPending = isCreatePending || isUpdatePending;

  const profileId = "profile" in data ? data.profile.id : data.id;
  const shortlist = shortlists.find((entry) => entry.profile.id === profileId);
  const isShortlistedAndSuggested = !!shortlist?.suggested;
  const isShortlistBooked = shortlist && LOCKED_SHORTLIST_STATES.includes(shortlist.state);

  const handleClick = () => {
    const suggestedMap: Record<ResourceTableOption, ShortlistSuggested> = {
      [ResourceTableOption.Matches]: "matches",
      [ResourceTableOption.AllResources]: "named_resource",
      [ResourceTableOption.Shortlists]: "shortlist"
    };

    if (shortlist) {
      updateShortlist({
        id: shortlist.id,
        data: { suggested: suggestedMap[option] }
      });
    } else {
      createShortlist({
        activityId: activity.id,
        profileId,
        suggested: suggestedMap[option],
        named: option === ResourceTableOption.AllResources
      });
    }
  };

  return (
    <Flex>
      {isShortlistedAndSuggested && <Pill text={t("suggested")} small />}
      {!isShortlistedAndSuggested && (
        <Button kind="text" onClick={handleClick} small disabled={disabled || isPending || isShortlistBooked}>
          {t("suggest")}
        </Button>
      )}
    </Flex>
  );
};
